(() => {
  'use strict';
  if (window.LabellaShoppingUX) return;
  window.LabellaShoppingUX = true;

  const text = (node, value) => { if (node.textContent !== value) node.textContent = value; };
  const node = (tag, className, label = '') => {
    const element = document.createElement(tag); element.className = className;
    if (label) element.textContent = label; return element;
  };
  const visible = element => !!element && !element.hidden && element.getBoundingClientRect().width > 0 &&
    element.getBoundingClientRect().height > 0 && getComputedStyle(element).visibility !== 'hidden';

  class ShoppingUX {
    constructor(info, config) {
      this.info = info; this.config = config; this.colorStates = new WeakMap();
      this.mobile = matchMedia('(max-width: 749px)');
      this.money = new Intl.NumberFormat('tr-TR', { style: 'currency', currency: config.currency || 'TRY' });
      this.bar = node('section', 'lb-ux-bar');
      this.bar.setAttribute('aria-label', 'Ürün satın alma');
      this.priceBox = node('div', 'lb-ux-price');
      this.caption = node('span', 'lb-ux-caption');
      this.price = node('strong', 'lb-ux-amount');
      this.priceBox.setAttribute('aria-live', 'polite'); this.priceBox.setAttribute('aria-atomic', 'true');
      this.priceBox.append(this.caption, this.price);
      this.button = node('button', 'lb-ux-add', 'Sepete ekle'); this.button.type = 'button';
      this.bar.append(this.priceBox, this.button);
      this.spacer = node('div', 'lb-ux-spacer'); this.spacer.setAttribute('aria-hidden', 'true');
      document.body.append(this.spacer, this.bar);
      this.button.addEventListener('click', () => this.submit());
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => { this.frame = 0; this.sync(); });
      };
      this.observer = new MutationObserver(this.schedule);
      this.observer.observe(info, { childList: true, characterData: true, subtree: true,
        attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'aria-pressed', 'class', 'hidden', 'value'] });
      this.pageObserver = new MutationObserver(this.schedule);
      this.pageObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      this.pageObserver.observe(document.body, { childList: true, attributes: true, attributeFilter: ['class'] });
      info.addEventListener('input', this.schedule); info.addEventListener('change', this.schedule);
      document.addEventListener('click', this.schedule);
      document.addEventListener('keydown', event => { if (event.key === 'Escape') this.schedule(); });
      this.mobile.addEventListener('change', this.schedule);
      window.addEventListener('resize', this.schedule, { passive: true });
      window.visualViewport?.addEventListener('resize', this.schedule, { passive: true });
      this.resizeObserver = new ResizeObserver(this.schedule); this.resizeObserver.observe(this.bar);
      this.sync();
    }

    compactColors() {
      for (const grid of this.info.querySelectorAll('lb-lens-picker[data-ready] .lb-colors')) {
        const colors = [...grid.querySelectorAll('[data-lb-color]')];
        if (colors.length <= 4) continue;
        if (!grid.classList.contains('lb-ux-color-grid')) grid.classList.add('lb-ux-color-grid');
        let state = this.colorStates.get(grid);
        if (!state) {
          const toggle = node('button', 'lb-ux-colors-toggle'); toggle.type = 'button';
          if (!grid.id) grid.id = `lb-ux-colors-${this.info.dataset.section || this.info.id}`;
          toggle.setAttribute('aria-controls', grid.id);
          state = { expanded: false, toggle }; this.colorStates.set(grid, state);
          toggle.addEventListener('click', () => {
            state.expanded = !state.expanded; this.compactColors();
          });
          grid.after(toggle);
        }
        const selected = colors.find(button => button.getAttribute('aria-pressed') === 'true');
        const initial = colors.slice(0, 4);
        // Keep an explicitly selected later color visible without showing 5 cards.
        if (selected && !initial.includes(selected)) initial.splice(3, 1, selected);
        colors.forEach(color => color.classList.toggle('lb-ux-color-hidden', !state.expanded && !initial.includes(color)));
        state.toggle.setAttribute('aria-expanded', String(state.expanded));
        text(state.toggle, state.expanded ? 'Daha az renk göster' : `Tüm renkleri göster (${colors.length})`);
      }
    }

    controls() {
      const form = this.info.querySelector('product-form form');
      return { form, button: form?.querySelector('button[type="submit"],input[type="submit"]'),
        helper: this.info.querySelector('lb-lens-picker[data-ready]') };
    }

    nativeVariant(form) {
      const id = form?.querySelector('[name="id"]')?.value;
      try {
        const selected = this.info.querySelector('variant-selects [data-selected-variant]');
        const variant = selected ? JSON.parse(selected.textContent) : this.config.initialVariant;
        return variant && String(variant.id) === String(id) && Number.isFinite(variant.price) ? variant : null;
      } catch { return null; }
    }

    sync() {
      if (!this.info.isConnected) { this.bar.hidden = true; this.spacer.hidden = true; return; }
      this.compactColors();
      const { form, button, helper } = this.controls();
      const preview = document.getElementById('PBarNextFrameWrapper');
      if (preview && this.preview !== preview) { this.preview = preview; this.resizeObserver.observe(preview); }
      const previewHeight = visible(preview) ? preview.getBoundingClientRect().height : 0;
      const keyboard = window.visualViewport && innerHeight - window.visualViewport.height > 150;
      const overlay = document.documentElement.classList.contains('lb-confirmation-open') ||
        !!document.querySelector('cart-drawer.active, cart-notification.active, menu-drawer details[open], header-drawer details[open], details-modal details[open], product-modal[open]');
      const active = this.mobile.matches && !!button && !keyboard && !overlay && previewHeight < 180;
      this.bar.hidden = !active; this.spacer.hidden = !active;
      document.body.classList.toggle('lb-ux-sticky-active', active);
      document.documentElement.style.setProperty('--lb-ux-preview-offset', `${Math.ceil(previewHeight)}px`);
      const loading = !!button && (button.classList.contains('loading') || button.getAttribute('aria-disabled') === 'true');
      this.intent = 'add';
      this.button.disabled = !button || loading;
      let label = loading ? 'Ekleniyor…' : 'Sepete ekle';
      let amount = '', caption = '';
      if (helper) {
        if (helper.pending || helper.locked) {
          amount = 'Kontrol ediliyor…'; label = helper.locked || loading ? 'Ekleniyor…' : 'Kontrol ediliyor…';
          this.button.disabled = true;
        } else if (helper.canSubmit()) {
          // Reuse the same validated lines as the existing two-eye cart form.
          const lines = helper.getLines();
          const boxes = lines.reduce((sum, line) => sum + line.quantity, 0);
          amount = this.money.format(lines.reduce((sum, line) => sum + line.price * line.quantity, 0) / 100);
          caption = `${boxes} kutu toplamı`;
          this.button.disabled = loading || !!button.disabled;
        } else {
          const missing = !helper.state.first || (helper.state.mode === 'different' && !helper.state.second);
          this.intent = 'selection'; label = missing ? 'Numara seç' : 'Seçimi kontrol et';
          amount = missing ? 'Numaranızı seçin' : 'Seçimi kontrol edin';
          this.button.disabled = loading;
        }
      } else {
        const variant = this.nativeVariant(form);
        const quantityInput = this.info.querySelector('.quantity__input');
        const quantity = Number(quantityInput?.value || form?.querySelector('[name="quantity"]')?.value || 1);
        if (variant && Number.isSafeInteger(quantity) && quantity > 0) {
          amount = this.money.format(variant.price * quantity / 100); caption = `${quantity} adet toplamı`;
        } else {
          const prices = this.info.querySelectorAll('[id^="price-"] .price-item--sale,[id^="price-"] .price-item--regular');
          amount = [...prices].find(visible)?.textContent.trim() || 'Ürün fiyatını inceleyin';
          caption = 'Birim fiyat';
        }
        this.button.disabled = loading || !button || !!button.disabled;
        if (button?.disabled && !loading) label = button.querySelector('span')?.textContent.trim() || 'Kullanılamıyor';
      }
      text(this.caption, caption); text(this.price, amount); text(this.button, label);
      if (active) {
        const height = Math.ceil(this.bar.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--lb-ux-bar-height', `${height}px`);
      }
    }

    submit() {
      this.sync();
      if (this.button.disabled) return;
      const { form, button, helper } = this.controls();
      if (!button || !form) return;
      if (this.intent === 'selection' && helper) {
        helper.reportSelectionError();
        const field = helper.powerFields?.find(({ select, row }) => !row.hidden && !select.value)?.select || helper.status;
        field?.scrollIntoView({ block: 'center', behavior: 'smooth' }); return;
      }
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      // Forward exactly one native click: existing validation, prescription
      // properties, two-line payload, iCart and confirmation remain in charge.
      button.click(); this.sync();
    }
  }

  const start = () => {
    const configNode = document.getElementById('lb-shopping-ux-config');
    const info = document.querySelector('product-info[id^="MainProduct-"]');
    if (!configNode || !info) return;
    try { new ShoppingUX(info, JSON.parse(configNode.textContent)); }
    catch (error) { console.error('Labella shopping UX:', error); }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
